import React, { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import {
  Loader2,
  AlertCircle,
  ExternalLink,
  Play,
  Phone,
  Mail,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileUpload } from "@/components/ui/FileUpload";
import { supabase } from "@/lib/supabase";
import { AIContentRenderer } from "@/components/ai-assistant/AIContentRenderer";
import { OutputHeader } from "@/components/ai-assistant/OutputHeader";
import { useToast } from "@/hooks/use-toast";
import { LucideIconByName, IconMap } from "@/components/ui/lucide-icons";
import { SmartIcon } from "@/components/ui/SmartIcon";
import { useIsMobile } from "@/hooks/use-mobile";

interface PublicAssistant {
  ai_assistant: {
    id: string;
    name: string;
    title: string;
    description: string;
    category?: string;
    icon_url?: string;
    banner?: string;
    form_fields?: any[];
    base_instructions?: string;
    instructions?: string;
    zod_schema?: any;
    schema?: any;
    model?: string;
    temperature?: number;
    max_output_tokens?: number;
    max_tokens?: number;
  };
  ai_assistant_instance?: {
    id: string;
    title?: string; // Fixed: was custom_name, should be title
    description?: string; // Added missing field
    icon_url?: string; // Added missing field
    cta_label?: string; // Added missing field
    custom_instructions?: string;
    custom_form_fields?: any[];
    custom_zod_schema?: any;
  };
  organization: {
    id?: string;
    name: string;
    logo_url?: string;
    brand_color?: string;
    website?: string;
    contact_info?: any;
  };
  organization_id?: string;
}

export const PublicAssistantView: React.FC = () => {
  const { publicId } = useParams<{ publicId: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get("embed") === "true";
  const hideHeader = searchParams.get("hideHeader") === "true" || isEmbedded;
  const isMobile = useIsMobile();

  const [assistant, setAssistant] = useState<PublicAssistant | null>(null);
  const [mode, setMode] = useState<"output" | "assistant">("assistant");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [generatedContent, setGeneratedContent] = useState<any>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();

  // Track timeout IDs for cleanup
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const fetchPublicAssistant = async () => {
      if (!publicId) {
        setError("No public ID provided");
        setIsLoading(false);
        return;
      }

      try {
        console.log("Fetching public content:", publicId);

        const isOutputRoute = location.pathname.includes("/public/output/");
        setMode(isOutputRoute ? "output" : "assistant");

        const { data, error: functionError } = await supabase.functions.invoke(
          "get-public-assistant-output",
          {
            body: { publicId },
          },
        );

        if (functionError) {
          console.error("Error from Edge Function:", functionError);
          throw new Error(
            functionError.message || "Failed to fetch assistant info",
          );
        }

        if (!data || !data.data || !data.data.output) {
          console.error("Invalid response from Edge Function:", data);
          throw new Error("Assistant not found or not public");
        }

        // Extract assistant info from the output data
        const outputData = data.data.output;
        const assistantInfo: PublicAssistant = {
          ai_assistant: outputData.ai_assistant,
          ai_assistant_instance: outputData.ai_assistant_instance,
          organization: outputData.organization,
        };

        console.log("Found public assistant:", assistantInfo);
        setAssistant(assistantInfo);

        // Initialize form data only for interactive assistant mode
        const fields = isOutputRoute
          ? []
          : outputData.ai_assistant_instance?.custom_form_fields ||
            outputData.ai_assistant?.form_fields ||
            [];
        const initialFormData: Record<string, any> = {};
        fields.forEach((field: any) => {
          initialFormData[field.label] = field.default_value || "";
        });
        if (isOutputRoute && outputData.input_data) {
          setFormData(outputData.input_data as Record<string, any>);
        } else {
          setFormData(initialFormData);
        }

        // In output mode, display the saved output immediately (support legacy shapes)
        if (isOutputRoute) {
          const rowTitle = (outputData as any)?.output_title as
            | string
            | undefined;
          const contentRaw =
            (outputData as any)?.structured_output ??
            (outputData as any)?.output_data ??
            null;

          if (typeof contentRaw === "string") {
            setGeneratedContent({
              output_title: rowTitle,
              content: contentRaw,
            });
          } else if (contentRaw && typeof contentRaw === "object") {
            const hasTitle = (contentRaw as any).output_title;
            setGeneratedContent(
              hasTitle || !rowTitle
                ? (contentRaw as any)
                : { output_title: rowTitle, ...(contentRaw as any) },
            );
          } else {
            setGeneratedContent(null);
          }
        }
      } catch (err: any) {
        console.error("Error fetching public assistant:", err);
        setError(err.message || "Failed to load assistant");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPublicAssistant();
  }, [publicId, location.pathname]);

  const handleInputChange = (name: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGenerate = async () => {
    if (!assistant) return;

    try {
      setIsGenerating(true);
      setGeneratedContent(null);

      const instanceId = assistant.ai_assistant_instance?.id;
      const assistantId = assistant.ai_assistant.id;
      const organizationId =
        assistant.organization.id || assistant.organization_id;

      if (!organizationId) {
        throw new Error("Organization ID is required for generation");
      }

      // Prepare assistant config (match authenticated view exactly)
      const assistantConfig = {
        baseInstructions:
          assistant.ai_assistant_instance?.custom_instructions ||
          assistant.ai_assistant.base_instructions,
        customInstructions:
          assistant.ai_assistant_instance?.custom_instructions,
        zodSchema:
          assistant.ai_assistant_instance?.custom_zod_schema ||
          assistant.ai_assistant.zod_schema,
        formFields:
          assistant.ai_assistant_instance?.custom_form_fields ||
          assistant.ai_assistant.form_fields,
        // Ensure model and related options are sent to the Edge Function
        model: assistant.ai_assistant.model,
        generation_mode:
          (assistant.ai_assistant as any).generation_mode || "object",
        temperature: assistant.ai_assistant.temperature,
        max_output_tokens: assistant.ai_assistant.max_output_tokens,
      };

      // Validate all required fields are present
      if (!assistantId || !organizationId || !assistantConfig) {
        throw new Error("Missing required fields for generation");
      }

      if (
        !assistantConfig.baseInstructions ||
        !assistantConfig.zodSchema ||
        !assistantConfig.formFields
      ) {
        throw new Error("Invalid assistant configuration");
      }

      // Create request body (match authenticated view exactly - no userId for anonymous)
      const requestBody = {
        assistantId,
        instanceId: instanceId || null, // Allow null for optional instanceId
        inputData: formData,
        organizationId,
        assistantConfig,
      };

      console.log("🚀 Starting generation with request body:", requestBody);
      console.log(
        "🌐 Using Supabase URL:",
        (import.meta as any).env.VITE_SUPABASE_URL,
      );
      console.log(
        "🔑 Using anon key (first 10 chars):",
        (import.meta as any).env.VITE_SUPABASE_ANON_KEY?.substring(0, 10),
      );

      // First, test if we can reach the edge function with OPTIONS request
      try {
        console.log("🧪 Testing OPTIONS request to edge function...");
        const optionsResponse = await fetch(
          `${(import.meta as any).env.VITE_SUPABASE_URL}/functions/v1/generate-with-assistant`,
          {
            method: "OPTIONS",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${(import.meta as any).env.VITE_SUPABASE_ANON_KEY}`,
            },
          },
        );
        console.log(
          "🧪 OPTIONS response:",
          optionsResponse.status,
          optionsResponse.statusText,
        );
      } catch (optionsError) {
        console.error("🧪 OPTIONS request failed:", optionsError);
      }

      // Try with a shorter timeout first
      const controller = new AbortController();
      timeoutRef.current = setTimeout(() => {
        console.log("⏰ Request timeout after 10 seconds - aborting");
        controller.abort();
      }, 10000); // 10 second timeout for debugging

      // Start streaming generation (exact copy from authenticated view)
      const response = await fetch(
        `${(import.meta as any).env.VITE_SUPABASE_URL}/functions/v1/generate-with-assistant`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${(import.meta as any).env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify(requestBody),
          signal: controller.signal,
        },
      );

      // Clear timeout if request completes
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      console.log("📡 Response status:", response.status, response.statusText);
      console.log(
        "📡 Response headers:",
        Object.fromEntries(response.headers.entries()),
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Response error:", errorText);
        throw new Error(
          `Generation failed: ${response.statusText} - ${errorText}`,
        );
      }

      // Handle streaming response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error("Failed to get response reader");
      }

      console.log("🔄 Starting to read streaming response...");
      let buffer = "";
      let streamMode: "object" | "text" = "object";
      let textAccumulator = "";
      let chunkCount = 0;

      while (true) {
        const { done, value } = await reader.read();
        chunkCount++;

        if (done) {
          console.log("✅ Streaming completed after", chunkCount, "chunks");
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        console.log(`📦 Chunk ${chunkCount}:`, chunk);
        buffer += chunk;

        // Process complete SSE messages
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log("📊 Parsed data:", data);

              switch (data.type) {
                case "meta":
                  streamMode = data.mode === "text" ? "text" : "object";
                  if (
                    data.outputTitle &&
                    typeof data.outputTitle === "string"
                  ) {
                    setGeneratedContent((prev: any) => ({
                      ...(typeof prev === "object" && prev !== null
                        ? prev
                        : {}),
                      output_title: data.outputTitle,
                    }));
                  }
                  break;
                case "text-delta":
                  textAccumulator += data.data || "";
                  setGeneratedContent((prev: any) => {
                    const prevTitle =
                      typeof prev === "object" && prev !== null
                        ? prev.output_title
                        : undefined;
                    return prevTitle
                      ? { output_title: prevTitle, content: textAccumulator }
                      : textAccumulator;
                  });
                  break;
                case "partial":
                  console.log("🔄 Partial update:", data.data);
                  // Update with partial content
                  setGeneratedContent(data.data);
                  break;

                case "complete":
                  console.log("✅ Complete:", data.data);
                  // Set final content
                  if (streamMode === "text") {
                    const finalText =
                      typeof data.data === "string"
                        ? data.data
                        : textAccumulator;
                    setGeneratedContent((prev: any) => {
                      const prevTitle =
                        typeof prev === "object" && prev !== null
                          ? prev.output_title
                          : undefined;
                      return prevTitle
                        ? { output_title: prevTitle, content: finalText }
                        : finalText;
                    });
                  } else {
                    setGeneratedContent(data.data);
                  }
                  toast({
                    title: "Content Generated!",
                    description:
                      "Your content has been generated successfully.",
                  });
                  break;

                case "error":
                  console.error("❌ Stream error:", data.error);
                  throw new Error(data.error);
              }
            } catch (parseError) {
              console.error(
                "Error parsing streaming data:",
                parseError,
                "Raw line:",
                line,
              );
            }
          } else if (line.trim()) {
            console.log("📝 Non-data line:", line);
          }
        }
      }
    } catch (error: any) {
      console.error("Generation error:", error);

      let errorMessage = "Failed to generate content. Please try again.";
      if (error.name === "AbortError") {
        errorMessage = "Request timed out. Please try again.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        variant: "destructive",
        title: "Generation Failed",
        description: errorMessage,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const renderFormField = (field: any) => {
    const value =
      formData[field.label] ||
      (field.type === "file" || field.type === "image" ? [] : "");

    switch (field.type) {
      case "text":
        return (
          <Input
            value={value}
            onChange={(e) => handleInputChange(field.label, e.target.value)}
            placeholder={field.placeholder || ""}
          />
        );
      case "textarea":
        return (
          <Textarea
            value={value}
            onChange={(e) => handleInputChange(field.label, e.target.value)}
            placeholder={field.placeholder || ""}
            rows={4}
          />
        );
      case "select":
        return (
          <Select
            value={value}
            onValueChange={(val) => handleInputChange(field.label, val)}
          >
            <SelectTrigger>
              <SelectValue placeholder={field.placeholder || "Select..."} />
            </SelectTrigger>
            <SelectContent className="z-[9999] bg-white border shadow-lg">
              {field.options?.map((option: any, optionIndex: number) => (
                <SelectItem
                  key={optionIndex}
                  value={option}
                  className="hover:bg-gray-100"
                >
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "file":
        return (
          <FileUpload
            onFilesChange={(files) => handleInputChange(field.label, files)}
            maxFiles={5}
            value={value}
            mode="file"
          />
        );
      case "image":
        return (
          <FileUpload
            onFilesChange={(files) => handleInputChange(field.label, files)}
            maxFiles={5}
            value={value}
            mode="image"
          />
        );
      default:
        return (
          <Input
            value={value}
            onChange={(e) => handleInputChange(field.label, e.target.value)}
            placeholder={field.placeholder || ""}
          />
        );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading AI assistant...</p>
        </div>
      </div>
    );
  }

  if (error || !assistant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Assistant Not Available
              </h3>
              <p className="text-gray-600 mb-4">
                {error ||
                  "This AI assistant could not be found or is no longer public."}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const brandColor = assistant.organization.brand_color || "#060520";
  const assistantName =
    assistant.ai_assistant_instance?.title ||
    assistant.ai_assistant?.title ||
    "Untitled Assistant";
  const assistantDescription =
    assistant.ai_assistant_instance?.description ||
    assistant.ai_assistant?.description ||
    "No description available";
  const assistantIcon =
    assistant.ai_assistant_instance?.icon_url ||
    assistant.ai_assistant?.icon_url;
  const assistantCtaLabel =
    assistant.ai_assistant_instance?.cta_label ||
    (assistant.ai_assistant as any)?.cta_label ||
    "Generate";
  const formFields =
    assistant.ai_assistant_instance?.custom_form_fields ||
    assistant.ai_assistant?.form_fields ||
    [];
  const displayName = assistant.organization.name || "Assistant";
  const avatarUrl = assistant.organization.logo_url;
  const contactInfo = assistant.organization.contact_info as
    | { phone?: string; email?: string }
    | undefined;

  const customStyles = {
    "--primary": brandColor,
    "--primary-foreground":
      brandColor === "#ffffff" || brandColor === "#fff" ? "#000000" : "#ffffff",
  } as React.CSSProperties;

  // Layout similar to PublicAgentLayout
  const containerStyle = isEmbedded
    ? {}
    : { backgroundColor: `${brandColor}17` };

  return (
    <div
      style={{
        ...customStyles,
        ...containerStyle,
      }}
      className={
        isEmbedded
          ? "w-full h-full flex flex-col bg-white overflow-hidden"
          : "min-h-screen h-screen flex flex-col"
      }
    >
      <div
        className={
          isEmbedded
            ? "flex-1 w-full h-full flex flex-col overflow-hidden"
            : "bg-white overflow-hidden w-full h-full flex flex-col shadow-lg rounded-lg"
        }
      >
        {/* Header - styled like ChatHeader - Now Sticky */}
        {!hideHeader && (
          <div className="sticky top-0 z-50 flex flex-col">
            <div
              className="text-white px-5 py-4 flex items-center justify-between"
              style={{ backgroundColor: brandColor }}
            >
              <div className="flex items-center gap-4">
                {avatarUrl ? (
                  typeof avatarUrl === "string" && IconMap[avatarUrl] ? (
                    <div className="h-10 w-10 rounded-full bg-white p-1 flex items-center justify-center">
                      <LucideIconByName
                        name={avatarUrl}
                        size={20}
                        className="text-gray-700"
                      />
                    </div>
                  ) : (
                    <img
                      src={avatarUrl}
                      alt={displayName}
                      className="h-10 w-10 object-contain rounded-full bg-white p-1"
                    />
                  )
                ) : null}
                <div>
                  <h2 className="font-semibold text-sm text-white">
                    {displayName}
                  </h2>
                </div>
              </div>

              {/* Contact buttons - removed as requested */}
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {isMobile ? (
            // Mobile layout - Fixed scrolling and sticky button
            <div className="flex-1 flex flex-col">
              {/* Scrollable Form Section */}
              <div className="flex-1 p-4 space-y-6 overflow-y-auto">
                {/* Banner and content area */}
                <div className="relative -mt-4 mb-6">
                  {/* Banner - full width */}
                  <div className="-mx-4">
                    {/* Banner */}
                    {assistant.ai_assistant.banner ? (
                      <div
                        className="h-32 w-full bg-cover bg-center bg-no-repeat"
                        style={{
                          backgroundImage: `url(${assistant.ai_assistant.banner})`,
                        }}
                      />
                    ) : (
                      <div
                        className={`h-32 w-full bg-gradient-to-r ${(() => {
                          const c = assistant.ai_assistant.category || "";
                          if (c.includes("Sales"))
                            return "from-green-500 to-green-600";
                          if (c.includes("Marketing"))
                            return "from-blue-500 to-blue-600";
                          if (c.includes("Daily work"))
                            return "from-amber-500 to-amber-600";
                          if (c.includes("Customer Engagement"))
                            return "from-cyan-500 to-cyan-600";
                          if (c.includes("Operations"))
                            return "from-purple-500 to-purple-600";
                          if (c.includes("Legal"))
                            return "from-rose-500 to-rose-600";
                          if (c.includes("Finance"))
                            return "from-indigo-500 to-indigo-600";
                          if (c.includes("HR"))
                            return "from-emerald-500 to-emerald-600";
                          return "from-gray-500 to-gray-600";
                        })()}`}
                      />
                    )}
                  </div>

                  {/* Icon overlapping banner - mid-cut effect */}
                  <div
                    className="flex justify-center"
                    style={{ marginTop: "-30px" }}
                  >
                    <div
                      className="bg-white rounded-full"
                      style={{ padding: "0.15rem" }}
                    >
                      <SmartIcon
                        iconUrl={assistantIcon}
                        size={60}
                        className="rounded-lg"
                        alt={`${assistantName} icon`}
                      />
                    </div>
                  </div>

                  {/* Assistant Info - centered */}
                  <div className="text-center pt-4">
                    <h1
                      className="text-2xl font-bold mb-1"
                      style={{ color: brandColor }}
                    >
                      {assistantName}
                    </h1>
                    <p className="text-gray-600">{assistantDescription}</p>
                  </div>
                </div>

                {/* Form with 24px spacing and bottom padding for sticky button */}
                <div className="space-y-6 pb-20">
                  {formFields.map((field: any) => (
                    <div key={field.label} className="space-y-2">
                      <Label
                        htmlFor={field.label}
                        className="text-sm font-medium"
                      >
                        {field.label}
                        {field.required && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </Label>
                      <div className="mt-2">{renderFormField(field)}</div>
                      {field.description && (
                        <p className="text-xs text-gray-500">
                          {field.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Sticky Generation Button - Always Visible */}
              <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white shadow-lg">
                <Button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className="w-full flex items-center space-x-2 text-white h-11"
                  style={{ backgroundColor: brandColor }}
                  size="lg"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      <span>{assistantCtaLabel}</span>
                    </>
                  )}
                </Button>
              </div>

              {/* Generated Content Section - Fixed Height */}
              {(isGenerating || generatedContent) && (
                <div className="flex-1 p-4 overflow-y-auto">
                  <div className="max-w-[750px] mx-auto">
                    <OutputHeader
                      title={
                        (generatedContent as any)?.output_title ||
                        "Generated Content"
                      }
                      formFields={formFields}
                      formData={formData}
                    />
                    {isGenerating ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 text-yellow-600">
                          <div className="h-2 w-2 bg-yellow-500 rounded-full animate-pulse"></div>
                          <span className="text-sm font-medium">
                            Generating content...
                          </span>
                        </div>
                        {generatedContent && (
                          <div className="border-2 border-dashed border-yellow-200 rounded-lg p-4">
                            <AIContentRenderer
                              content={
                                (generatedContent as any)?.content ||
                                generatedContent
                              }
                              brandColor={brandColor}
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <AIContentRenderer
                          content={
                            (generatedContent as any)?.content ||
                            generatedContent
                          }
                          brandColor={brandColor}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Desktop layout - Fixed scrolling and sticky button (using flex like authenticated view)
            <div className="flex-1 flex overflow-hidden">
              {/* Left Column: Form - Fixed Structure (hidden for read-only output mode) */}
              {mode === "assistant" && (
                <div className="min-w-[500px] w-[500px] border-r border-gray-200 bg-white flex flex-col">
                  <div className="flex-1 p-6 space-y-6 overflow-y-auto">
                    {/* Banner and content area */}
                    <div className="relative -mt-6 mb-6">
                      {/* Banner - full width */}
                      <div className="-mx-6">
                        {/* Banner */}
                        {assistant.ai_assistant.banner ? (
                          <div
                            className="h-32 w-full bg-cover bg-center bg-no-repeat"
                            style={{
                              backgroundImage: `url(${assistant.ai_assistant.banner})`,
                            }}
                          />
                        ) : (
                          <div
                            className={`h-32 w-full bg-gradient-to-r ${
                              assistant.ai_assistant.category?.includes(
                                "Marketing",
                              )
                                ? "from-blue-500 to-blue-600"
                                : assistant.ai_assistant.category?.includes(
                                      "Sales",
                                    )
                                  ? "from-green-500 to-green-600"
                                  : assistant.ai_assistant.category?.includes(
                                        "Daily work",
                                      )
                                    ? "from-amber-500 to-amber-600"
                                    : "from-gray-500 to-gray-600"
                            }`}
                          />
                        )}
                      </div>

                      {/* Icon overlapping banner - mid-cut effect */}
                      <div className="absolute left-0" style={{ top: "98px" }}>
                        <div
                          className="bg-white rounded-full"
                          style={{ padding: "0.15rem" }}
                        >
                          <SmartIcon
                            iconUrl={assistantIcon}
                            size={60}
                            className="rounded-lg"
                            alt={`${assistantName} icon`}
                          />
                        </div>
                      </div>

                      {/* Assistant Info - aligned with form fields */}
                      <div className="pt-12">
                        <h1
                          className="text-xl font-semibold mb-1"
                          style={{ color: brandColor }}
                        >
                          {assistantName}
                        </h1>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {assistantDescription}
                        </p>
                      </div>
                    </div>

                    {/* Form with 24px spacing and bottom padding for sticky button */}
                    <div className="space-y-6 pb-20">
                      {formFields.map((field: any) => (
                        <div key={field.label} className="space-y-2">
                          <Label
                            htmlFor={field.label}
                            className="text-sm font-medium"
                          >
                            {field.label}
                            {field.required && (
                              <span className="text-red-500 ml-1">*</span>
                            )}
                          </Label>
                          <div className="mt-2">{renderFormField(field)}</div>
                          {field.description && (
                            <p className="text-xs text-gray-500">
                              {field.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Sticky Generation Button - Always Visible */}
                  <div className="flex-shrink-0 p-6 border-t border-gray-200 bg-white shadow-lg">
                    <Button
                      onClick={handleGenerate}
                      disabled={isGenerating}
                      className="w-full flex items-center space-x-2 text-white h-11"
                      style={{ backgroundColor: brandColor }}
                      size="lg"
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Generating...</span>
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4" />
                          <span>{assistantCtaLabel}</span>
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Right Column: Content */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-6">
                  <div className="max-w-[750px] mx-auto">
                    {mode === "assistant" && (
                      <OutputHeader
                        title={
                          (generatedContent as any)?.output_title ||
                          "Generated Content"
                        }
                        formFields={formFields}
                        formData={formData}
                      />
                    )}

                    {isGenerating ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 text-yellow-600">
                          <div className="h-2 w-2 bg-yellow-500 rounded-full animate-pulse"></div>
                          <span className="text-sm font-medium">
                            Generating content...
                          </span>
                        </div>
                        {generatedContent && (
                          <div className="border-2 border-dashed border-yellow-200 rounded-lg p-4">
                            <AIContentRenderer
                              content={
                                (generatedContent as any)?.content ||
                                generatedContent
                              }
                              brandColor={brandColor}
                            />
                          </div>
                        )}
                      </div>
                    ) : generatedContent ? (
                      <div className="space-y-2">
                        <AIContentRenderer
                          content={
                            (generatedContent as any)?.content ||
                            generatedContent
                          }
                          brandColor={brandColor}
                        />
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <div className="mb-4">
                          <FileText className="h-12 w-12 mx-auto text-gray-300" />
                        </div>
                        {mode === "assistant" ? (
                          <p className="text-sm">
                            Fill out the form and click generate to see content
                            here
                          </p>
                        ) : (
                          <p className="text-sm">
                            This shared output will appear here.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
